import React, { useState, useEffect } from 'react';
import { Bell, CheckCircle, XCircle, Star, ShoppingBag } from 'lucide-react';
import CreatorLayout from './CreatorLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTH_TOKEN_KEY = 'ivisora_auth_token';

const ICON_MAP = {
  template_approved: CheckCircle,
  template_rejected: XCircle,
  template_review:   Star,
  template_purchased: ShoppingBag,
};

const NotificationRow = ({ n, onMarkRead }) => {
  const Icon = ICON_MAP[n.type] || Bell;
  const iconColor = n.type === 'template_approved' ? 'text-emerald-500'
    : n.type === 'template_rejected' ? 'text-red-500'
    : n.type === 'template_review' ? 'text-amber-500'
    : n.type === 'template_purchased' ? 'text-violet-500'
    : 'text-gray-400';

  return (
    <div
      className={`flex items-start gap-4 px-5 py-4 border-b border-gray-50 last:border-0 transition-colors ${
        !n.is_read ? 'bg-violet-50/30' : 'hover:bg-gray-50'
      }`}
    >
      <div className={`w-9 h-9 rounded-xl bg-white border border-gray-100 flex items-center justify-center flex-shrink-0 ${iconColor}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold text-gray-900 ${!n.is_read ? '' : 'opacity-70'}`}>
          {n.title || n.type}
        </p>
        <p className="text-sm text-gray-500 mt-0.5">{n.message}</p>
        <p className="text-xs text-gray-400 mt-1">
          {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {!n.is_read && (
          <>
            <span className="w-2 h-2 rounded-full bg-violet-500" />
            <button
              onClick={() => onMarkRead(n.id)}
              className="text-xs text-gray-400 hover:text-violet-600"
            >
              Mark read
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const CreatorNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) return;
    fetch(`${API_URL}/api/notifications/me?limit=50`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : { items: [] })
      .then(d => { setNotifications(Array.isArray(d) ? d : (d.items || [])); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleMarkRead = async (id) => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    await fetch(`${API_URL}/api/notifications/${id}/read`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
    setNotifications(ns => ns.map(n => n.id === id ? { ...n, is_read: true } : n));
  };

  const handleMarkAllRead = async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    await fetch(`${API_URL}/api/notifications/me/read-all`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
    setNotifications(ns => ns.map(n => ({ ...n, is_read: true })));
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <CreatorLayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-900">Notifications</h1>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="text-sm text-violet-600 font-semibold hover:text-violet-700"
          >
            Mark all as read
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-5 h-5 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-16">
            <Bell className="w-10 h-10 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No notifications yet</p>
          </div>
        ) : (
          notifications.map(n => (
            <NotificationRow key={n.id} n={n} onMarkRead={handleMarkRead} />
          ))
        )}
      </div>
    </CreatorLayout>
  );
};

export default CreatorNotifications;
